import { useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';

import Pagination from '@/components/common/Pagination/Pagination';
import AgGridLoadingOverlay
  from '@/components/common/TableGrid/CustomComponents/AgGridLoadingOverlay/AgGridLoadingOverlay';
import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import { ImportingToolDocumentWithUserName, ImportingToolPaginatedList }
  from './models/getPaginatedDocumentsResult.interface';
import { useAddQueryToUrl } from '@/components/common/TagTabs/useAddQueryToUrl';
import { browserHistory } from '@/routing/browserHistory';
import { IMPORTING_TOOL_UPLOADED_FILES_PER_PAGE } from '@/config/common.config';
import { useGetImportingToolFiles } from './hooks/useGetImportingToolFiles';
import { useColumns } from './columns';

const REFRESH_GRID_INTERVAL = 10000;
const processingStatuses = ['validating', 'importing'];

export default function ImportingToolUploadedFiles() {
  const gridRef = useRef<AgGridReact>(null);
  const columns = useColumns();
  const { getLinkWithQuery, getValue } = useAddQueryToUrl();
  const currentPage = parseInt(getValue('page') ?? '1', 10);

  const updateDataIfCurrentPageIsIncorrect = (
    response: ImportingToolPaginatedList<ImportingToolDocumentWithUserName>
  ) => {
    if (response && response.totalPages < currentPage && response.totalPages >= 1) {
      const routePath = getLinkWithQuery({ page: response.totalPages.toString() });
      browserHistory.push(routePath);
    }
  };

  const { files, isLoading, totalPages, getUploadedFiles } =
    useGetImportingToolFiles(updateDataIfCurrentPageIsIncorrect);

  const onClickPage = (page: number) => {
    const routePath = getLinkWithQuery({ page: page.toString() });
    browserHistory.push(routePath);
  };

  const hasProcessingStatuses: boolean = useMemo(() =>
    files.some(file => processingStatuses.includes(file.status.toLocaleLowerCase())), [files]);

  useEffect(() => {
    const setOverlay = () => {
      if (isLoading) {
        return gridRef.current?.api?.showLoadingOverlay();
      }

      if (!files?.length) {
        return gridRef.current?.api?.showNoRowsOverlay();
      }

      return gridRef.current?.api?.hideOverlay();
    };

    setOverlay();
  }, [isLoading, files]);

  useEffect(() => {
    getUploadedFiles(currentPage, IMPORTING_TOOL_UPLOADED_FILES_PER_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (hasProcessingStatuses) {
        getUploadedFiles(currentPage, IMPORTING_TOOL_UPLOADED_FILES_PER_PAGE);
      }
    }, REFRESH_GRID_INTERVAL);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProcessingStatuses]);

  return (
    <>
      <TableGrid
        ref={gridRef}
        columnDefs={columns}
        rowData={files}
        suppressMovableColumns={true}
        loadingOverlayComponent={AgGridLoadingOverlay}
        defaultColDef={{ sortable: false, resizable: false }}
      />
      <div className="mt-10">
        <div className="d-flex justify-content-end">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            disabled={isLoading}
            onClickPage={onClickPage}
          />
        </div>
      </div>
    </>
  );
}
